// Multi-page guide: creating a schedule from an open project.
//
// Two stages. The first waits on the project page for the user to open the
// setup window; the second runs inside it. The window is not a route of its
// own, so that stage is gated on its fields appearing.
const createScheduleFromProjectGuide = {
  id: 'create-schedule-from-project',
  kind: 'interactive',
  title: 'Create a new schedule from the Project page',
  summary: 'Name it, pick what to copy from, and choose what comes across.',
  version: 1,
  stages: [
    {
      id: 'project',
      match: '/dashboard/yawn/project/,!/dashboard/yawn/project/list,!/dashboard/yawn/project/new',
      // The setup window opens in place rather than navigating, so clicking
      // "Add New" is itself the signal to move to the next stage.
      advanceOnAction: true,
      waitingHint: 'Open a project to begin.',
      waitingAnchors: [
        { target: 'nav-Projects', hint: 'Open Projects from the menu, then open a project.' },
      ],
      steps: [
        {
          target: 'project-add-schedule',
          title: 'Add a new schedule',
          body: 'Click "Add New" to open the schedule setup window.',
          placement: 'left',
        },
      ],
    },
    {
      id: 'dialog',
      match: '/dashboard/yawn/project/,!/dashboard/yawn/project/list,!/dashboard/yawn/project/new',
      // The window opens on click rather than navigation.
      requires: 'schedule-new-name',
      // Cancel, Create or the backdrop closes the window and ends the guide.
      endsWithDialog: true,
      waitingHint: 'Click "Add New" to open the schedule setup window.',
      waitingAnchors: [
        {
          target: 'project-add-schedule',
          hint: 'Click "Add New" to open the schedule setup window.',
        },
      ],
      steps: [
        {
          target: 'schedule-new-name',
          // Advances on Next, not on the field's contents: there is no confirm
          // control here, so nothing can tell a half-typed name from a finished
          // one, and moving on early puts the popover over the field.
          title: 'Name your schedule',
          body: 'Give it a name you will recognise later, such as "External Fabric" or "Stage 3 Issue".',
          // The field sits at the top of a tall dialog, so a popover below it
          // lands on the field itself. Beside it instead.
          placement: 'right',
        },
        {
          target: 'schedule-new-baseline',
          // Advances on Next: picking a row is reversible, so the user may want
          // to change their choice before moving on.
          title: 'Choose what to start from',
          body: 'Pick a Master template, or switch the Masters filter to No to copy a schedule from one of your own projects.',
          tip: 'The new schedule starts as a copy of whatever you choose here.',
          placement: 'top',
        },
        {
          // Only "More settings..." is live here: the step is about that one
          // choice. Opening the panel jumps ahead to the settings step; Next
          // skips it and moves on to finishing.
          target: 'schedule-new-more-toggle',
          title: 'Set more options, or carry on',
          body: 'Open "More settings..." to choose exactly what comes across from the schedule you picked.',
          tip: 'Nothing more to set? Click Next to go to Create.',
          // Opening the panel moves on to the settings step, which follows.
          // Next is the other route: skip the settings and go to Create.
          // "More settings..." sits at the right of the window, so the popover
          // goes to its left, where there is room for it.
          placement: 'left',
          deferred: true,
          advanceWhen: 'expanded',
          nextTo: 'schedule-new-finish',
        },
        {
          // Either route reaches this step: Next from the branch above, or
          // opening the panel. Both lead on to the finishing step.
          //
          // This step describes the open panel, so it belongs to that state:
          // closing the panel again returns to the step that offers to open
          // it, rather than explaining contents the user can no longer see.
          holdsWhile: 'expanded',
          target: 'schedule-new-more-settings',
          title: 'Choose what comes across',
          body: 'Copy only ticked rows, pick which columns the schedule starts with, and choose which keep their contents.',
          tip: 'Leave these as they are to copy everything.',
          placement: 'top',
          deferred: true,
        },
        {
          // Last step: the guide ends when the user acts on it. Closing the
          // window ends the guide, which the deferred watcher handles.
          target: 'schedule-new-finish',
          title: 'Create the schedule',
          body: 'Click "Create" to add the schedule, or "Cancel" to close without creating it.',
          placement: 'top',
          deferred: true,
        },
      ],
    },
  ],
};

export default createScheduleFromProjectGuide;
